/**
 * «¿Cómo nos conoció?» (`how_known_us`): las opciones y el aviso (23/9/2026).
 *
 * DECIDIDO POR DIRECCIÓN (23/9/2026): es OBLIGATORIO al dar de alta un paciente,
 * al editar su ficha y al cerrar la LOPD, para que el indicador «Captación por
 * origen» no tenga casillas vacías. Para no bloquear a nadie cuando el
 * paciente de verdad no lo sabe hay una respuesta explícita, «No lo recuerda»,
 * que vale y es distinta de «Sin origen» (no haber preguntado).
 *
 * El servidor (`backend/services/origenes_captacion.py`) impone la regla y
 * contesta el mismo aviso. En pantalla es un DESPLEGABLE, no texto libre: el
 * KPI agrupa por estos textos y «instagram» e «Insta» serían dos orígenes
 * distintos.
 *
 * EL CATÁLOGO LO EDITA DIRECCIÓN (pestaña «Captación por origen») y aquí NO
 * hay copia de él: `useOpcionesComoNosConocio` pide los nombres vigentes al
 * servidor (`/api/origenes-captacion/nombres` con sesión,
 * `/api/public/origenes-captacion` en la LOPD de casa y del iPad), que es
 * quien los tiene. Mientras contesta —o si la petición falla— el desplegable
 * ofrece «No lo recuerda», así que nunca queda vacío, y el fallo no se guarda:
 * la siguiente vez que se abra se vuelve a pedir.
 */
import { useEffect, useState } from 'react';
import { api } from './api';

export const NO_LO_RECUERDA = 'No lo recuerda';

/** Lo que ofrece el desplegable hasta que contesta el servidor. */
export const OPCIONES_SIN_CATALOGO = [NO_LO_RECUERDA];

/** El mismo texto que el 400 del servidor. */
export const AVISO_FALTA_ORIGEN = 'Falta “Cómo nos conoció” (si el paciente no lo sabe, elige “No lo recuerda”)';

export const faltaOrigen = (v) => !String(v ?? '').trim();

/**
 * Las opciones del desplegable para un valor dado. Las fichas antiguas pueden
 * tener texto libre de antes («Recomendación», «Internet»): ese texto NO se pierde
 * al abrir la ficha. Sale el primero, marcado como valor actual, y se guarda
 * tal cual si no se cambia.
 */
export function opcionesComoNosConocio(valorActual, lista = OPCIONES_SIN_CATALOGO) {
  const actual = String(valorActual ?? '').trim();
  const opciones = lista.map((o) => ({ valor: o, etiqueta: o }));
  if (actual && !lista.includes(actual)) {
    return [{ valor: actual, etiqueta: `${actual} (valor actual)` }, ...opciones];
  }
  return opciones;
}

const URL_EQUIPO = '/api/origenes-captacion/nombres';
const URL_PUBLICA = '/api/public/origenes-captacion';
const VIGENCIA_MS = 5 * 60 * 1000;
const guardadas = {};

/** Los nombres que manda el servidor, limpios y con «No lo recuerda» al
 *  final; null si no sirven (vacío, forma rara). */
export function listaDelServidor(datos) {
  const lista = Array.isArray(datos) ? datos : (Array.isArray(datos?.nombres) ? datos.nombres : null);
  if (!lista) return null;
  const nombres = [...new Set(lista.map((n) => String(n ?? '').trim()).filter(Boolean))]
    .filter((n) => n.toLowerCase() !== NO_LO_RECUERDA.toLowerCase());
  return nombres.length ? [...nombres, NO_LO_RECUERDA] : null;
}

/** Una petición por página y cinco minutos; un fallo no se guarda. Con
 *  `forzar` se vuelve a preguntar (el servidor ha dicho que una opción ya no
 *  existe: dirección cambió el catálogo con la página abierta). */
export function cargarOpcionesComoNosConocio(publico = false, { forzar = false } = {}) {
  const url = publico ? URL_PUBLICA : URL_EQUIPO;
  const ya = guardadas[url];
  if (!forzar && ya && Date.now() - ya.desde < VIGENCIA_MS) return ya.promesa;
  const promesa = api.get(url)
    .then((r) => listaDelServidor(r?.data))
    .catch(() => null)
    .then((lista) => {
      if (!lista) delete guardadas[url];
      return lista;
    });
  guardadas[url] = { promesa, desde: Date.now() };
  return promesa;
}

/** Para las pruebas: olvidar lo pedido. */
export function olvidarOpcionesComoNosConocio() {
  Object.keys(guardadas).forEach((k) => { delete guardadas[k]; });
}

/**
 * Las opciones del desplegable: «No lo recuerda» al instante y las del
 * catálogo vigente en cuanto contesta el servidor. Subir `version` las vuelve
 * a pedir sin esperar a que caduquen.
 */
export function useOpcionesComoNosConocio({ publico = false, version = 0 } = {}) {
  const [opciones, setOpciones] = useState(OPCIONES_SIN_CATALOGO);
  useEffect(() => {
    let vivo = true;
    cargarOpcionesComoNosConocio(publico, { forzar: version > 0 }).then((lista) => {
      if (vivo && lista) setOpciones(lista);
    });
    return () => { vivo = false; };
  }, [publico, version]);
  return opciones;
}

/** El 400 del servidor cuando lo elegido ya no está en el catálogo. */
export const esOrigenQueYaNoExiste = (e) => e?.response?.status === 400
  && /ya no est[áa] entre las opciones/i.test(String(e?.response?.data?.detail || ''));

/**
 * En la LOPD del iPad y del enlace de casa elige el propio paciente: las
 * abreviaturas del catálogo («Rec. médica») se le enseñan enteras. Y la página
 * ENVÍA ese mismo texto (23/9/2026, segunda pasada del supervisor): lo que
 * firma tiene que decir exactamente lo que leyó, así que el papel guarda e
 * imprime «Recomendación médica». La ficha guarda el nombre del catálogo, que
 * lo pone el servidor (`origenes_captacion.ETIQUETAS_PARA_EL_PACIENTE`, la
 * copia de esta tabla: si se toca una, se toca la otra).
 */
const PARA_EL_PACIENTE = {
  'Google/Buscador': 'Google / buscador',
  'Rec. familiar/amigo': 'Recomendación de un familiar o amigo',
  'Rec. otro paciente': 'Recomendación de otro paciente',
  'Rec. médica': 'Recomendación médica',
};

export const etiquetaParaElPaciente = (valor) => PARA_EL_PACIENTE[valor] || valor;
